import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  BookOpen,
  Plus,
  RefreshCw,
  Trash2,
  Edit2,
  CheckCircle2,
  XCircle,
  Filter
} from "lucide-react";
import {
  createAssignment,
  getAllAssignments,
  updateAssignment,
  deleteAssignment
} from "../api/teachersubjects/teacherSubjectApi";
import {
  getAllAssignmentsPortal
} from "../api/teachersubjects/teacherSubjectPortalApi";

import {
  Button,
  Select,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  Modal,
  Badge,
  Skeleton,
  EmptyState,
  PageHeader,
  Checkbox
} from "../components/ui";
import {
  useToast
} from "../context/ToastContext";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";
const axiosInstance = axios.create({ baseURL: API, withCredentials: true });

const emptyForm = {
  staffId: "",
  subjectId: "",
  classId: "",
  sectionId: "",
  isActive: true,
};

const unwrapList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const TeacherSubject = ({ isPortal = false }) => {
  const [assignments, setAssignments] = useState([]);
  const [staffOptions, setStaffOptions] = useState([]);
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [classOptions, setClassOptions] = useState([]);
  const [sectionOptions, setSectionOptions] = useState([]);

  const [filters, setFilters] = useState({
    staffId: "",
    classId: "",
    sectionId: "",
    subjectId: "",
  });

  const [form, setForm] = useState(emptyForm);
  const [formSections, setFormSections] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const toast = useToast();

  const canWrite = !isPortal;

  const fetchAssignments = useCallback(async () => {
    setLoading(true);
    toast.error(null);
    try {
      const result = isPortal
        ? await getAllAssignmentsPortal(filters)
        : await getAllAssignments(filters);
      setAssignments(unwrapList(result));
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Failed to load assignments"
      );
    } finally {
      setLoading(false);
    }
  }, [filters, isPortal]);

  const fetchDropdowns = useCallback(async () => {
    try {
      const [staffRes, subjectRes, classRes] = await Promise.all([
        axiosInstance.get("/staff/all"),
        axiosInstance.get("/subjects/all"),
        axiosInstance.get("/classes/all"),
      ]);
      setStaffOptions(unwrapList(staffRes.data));
      setSubjectOptions(unwrapList(subjectRes.data));
      setClassOptions(unwrapList(classRes.data));
    } catch (err) {
      console.error("Dropdown load error", err);
    }
  }, []);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  useEffect(() => {
    fetchDropdowns();
  }, [fetchDropdowns]);

  // Sections for filter (depends on class)
  useEffect(() => {
    const load = async () => {
      if (!filters.classId) {
        setSectionOptions([]);
        return;
      }
      try {
        const res = await axiosInstance.get("/sections/all", {
          params: { classId: filters.classId },
        });
        setSectionOptions(unwrapList(res.data));
      } catch (err) {
        console.error(err);
        setSectionOptions([]);
      }
    };
    load();
  }, [filters.classId]);

  // Sections for create form (depends on class)
  useEffect(() => {
    const load = async () => {
      if (!form.classId) {
        setFormSections([]);
        return;
      }
      try {
        const res = await axiosInstance.get("/sections/all", {
          params: { classId: form.classId },
        });
        setFormSections(unwrapList(res.data));
      } catch (err) {
        console.error(err);
        setFormSections([]);
      }
    };
    load();
  }, [form.classId]);


  const handleFilterChange = (key, value) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "classId") next.sectionId = "";
      return next;
    });
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFormSections([]);
    setShowModal(true);
    toast.error(null);
  };

  const openEdit = (row) => {
    setEditingId(row._id);
    setForm({
      staffId: row.staffId?._id || row.staffId || "",
      subjectId: row.subjectId?._id || row.subjectId || "",
      classId: row.classId?._id || row.classId || "",
      sectionId: row.sectionId?._id || row.sectionId || "",
      isActive: row.isActive !== false,
    });
    setShowModal(true);
    toast.error(null);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setForm(emptyForm);
    setFormSections([]);
  };

  const handleFormChange = (key, value) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "classId") next.sectionId = "";
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canWrite) return;

    if (editingId) {
      if (!form.staffId || !form.subjectId) {
        toast.error("Teacher and subject are required");
        return;
      }
    } else if (!form.staffId || !form.subjectId || !form.classId || !form.sectionId) {
      toast.error("Teacher, subject, class and section are required");
      return;
    }

    setSaving(true);
    toast.error(null);
    try {
      if (editingId) {
        await updateAssignment(editingId, {
          staffId: form.staffId,
          subjectId: form.subjectId,
          isActive: form.isActive,
        });
        toast.success("Assignment updated");
      } else {
        await createAssignment({
          staffId: form.staffId,
          subjectId: form.subjectId,
          classId: form.classId,
          sectionId: form.sectionId,
        });
        toast.success("Assignment created");
      }
      closeModal();
      fetchAssignments();
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Failed to save assignment"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!canWrite) return;
    setSaving(true);
    toast.error(null);
    try {
      await deleteAssignment(id);
      toast.success("Assignment deleted");
      setDeleteConfirmId(null);
      fetchAssignments();
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Failed to delete assignment"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (row) => {
    if (!canWrite) return;
    try {
      await updateAssignment(row._id, { isActive: !row.isActive });
      toast.success(row.isActive ? "Marked inactive" : "Marked active");
      fetchAssignments();
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Failed to update status"
      );
    }
  };

  const staffLabel = (s) => {
    if (!s) return "—";
    if (typeof s === "string") return s;
    return s.name ? `${s.name}${s.staffCode ? ` (${s.staffCode})` : ""}` : "—";
  };

  const subjectLabel = (s) => {
    if (!s) return "—";
    if (typeof s === "string") return s;
    return s.name ? `${s.name}${s.code ? ` (${s.code})` : ""}` : "—";
  };

  const classLabel = (c) => {
    if (!c) return "—";
    if (typeof c === "string") return c;
    return c.className || "—";
  };

  const sectionLabel = (s) => {
    if (!s) return "—";
    if (typeof s === "string") return s;
    return s.sectionName || "—";
  };

  const sortedStaff = useMemo(
    () =>
      [...staffOptions].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "")
      ),
    [staffOptions]
  );

  const sortedSubjects = useMemo(
    () =>
      [...subjectOptions].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "")
      ),
    [subjectOptions]
  );

  // Mapped options for UI Select components
  const selectStaffOptions = useMemo(
    () => [
      { label: "All teachers", value: "" },
      ...sortedStaff.map((s) => ({ label: staffLabel(s), value: s._id })),
    ],
    [sortedStaff]
  );

  const selectSubjectOptions = useMemo(
    () => [
      { label: "All subjects", value: "" },
      ...sortedSubjects.map((s) => ({ label: subjectLabel(s), value: s._id })),
    ],
    [sortedSubjects]
  );

  const selectClassOptions = useMemo(
    () => [
      { label: "All classes", value: "" },
      ...classOptions.map((c) => ({ label: classLabel(c), value: c._id })),
    ],
    [classOptions]
  );

  const selectSectionOptions = useMemo(
    () => [
      { label: "All sections", value: "" },
      ...sectionOptions.map((s) => ({ label: sectionLabel(s), value: s._id })),
    ],
    [sectionOptions]
  );

  const selectFormSectionsOptions = useMemo(
    () => [
      { label: "Select section", value: "" },
      ...formSections.map((s) => ({ label: sectionLabel(s), value: s._id })),
    ],
    [formSections]
  );

  const filteredCount = assignments.length;

  const columns = [
    {
      key: "staff",
      header: "Teacher",
      render: (row) => (
        <span className="font-semibold text-neutral-900 dark:text-neutral-100">{staffLabel(row.staffId)}</span>
      ),
    },
    {
      key: "subject",
      header: "Subject",
      render: (row) => (
        <span className="inline-flex items-center gap-2">
          {row.subjectId?.color && (
            <span
              className="h-2.5 w-2.5 rounded-full shrink-0"
              style={{ backgroundColor: row.subjectId.color }}
            />
          )}
          <span className="font-medium">{subjectLabel(row.subjectId)}</span>
        </span>
      ),
    },
    {
      key: "class",
      header: "Class",
      render: (row) => classLabel(row.classId),
    },
    {
      key: "section",
      header: "Section",
      render: (row) => sectionLabel(row.sectionId),
    },
    {
      key: "status",
      header: "Status",
      render: (row) => (
        <Badge
          variant={row.isActive !== false ? "success" : "neutral"}
          className={canWrite ? "cursor-pointer hover:opacity-80" : "cursor-default"}
          onClick={canWrite ? () => handleToggleActive(row) : undefined}
        >
          {row.isActive !== false ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    ...(canWrite
      ? [
          {
            key: "actions",
            header: "Actions",
            render: (row) => (
              <div className="flex items-center justify-end gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => openEdit(row)}
                  leftIcon={<Edit2 className="h-3.5 w-3.5" />}
                >
                  Edit
                </Button>
                {deleteConfirmId === row._id ? (
                  <>
                    <Button
                      size="sm"
                      variant="danger"
                      disabled={saving}
                      onClick={() => handleDelete(row._id)}
                    >
                      Confirm
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setDeleteConfirmId(null)}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => setDeleteConfirmId(row._id)}
                    leftIcon={<Trash2 className="h-3.5 w-3.5" />}
                  >
                    Delete
                  </Button>
                )}
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6 antialiased">
      {/* Header */}
      <PageHeader
        title="Teacher–Subject Assignments"
        description={`Assign teachers to subjects for a class & section${isPortal ? " (view only)" : ""}`}
        action={
          canWrite && (
            <Button
              variant="primary"
              onClick={openCreate}
              leftIcon={<Plus className="h-4 w-4" />}
            >
              Assign Teacher
            </Button>
          )
        }
      />

      {/* Alerts */}
      
      

      {/* Filter Section */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 flex items-center gap-2">
            <Filter className="h-4 w-4 text-neutral-500 dark:text-neutral-400" /> Filter Assignments
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Select
            label="Teacher"
            options={selectStaffOptions}
            value={filters.staffId}
            onChange={(val) => handleFilterChange("staffId", val)}
          />
          <Select
            label="Subject"
            options={selectSubjectOptions}
            value={filters.subjectId}
            onChange={(val) => handleFilterChange("subjectId", val)}
          />
          <Select
            label="Class"
            options={selectClassOptions}
            value={filters.classId}
            onChange={(val) => handleFilterChange("classId", val)}
          />
          <Select
            label="Section"
            options={selectSectionOptions}
            value={filters.sectionId}
            disabled={!filters.classId}
            onChange={(val) => handleFilterChange("sectionId", val)}
          />
        </CardContent>
      </Card>

      {/* Assignments Table Card */}
      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-neutral-100 dark:border-neutral-800 pb-4">
          <CardTitle className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
            {loading ? (
              <Skeleton width={120} height={20} />
            ) : (
              `${filteredCount} assignment${filteredCount === 1 ? "" : "s"} found`
            )}
          </CardTitle>
          <Button
            size="sm"
            variant="secondary"
            onClick={fetchAssignments}
            leftIcon={<RefreshCw className="h-3.5 w-3.5" />}
          >
            Refresh
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <Table
            columns={columns}
            data={assignments}
            isLoading={loading}
            emptyState={
              <EmptyState
                icon={BookOpen}
                title="No teacher–subject assignments found"
                description="Try modifying filters or create a new assignment."
                action={
                  canWrite && (
                    <Button variant="primary" size="sm" onClick={openCreate}>
                      Create First Assignment
                    </Button>
                  )
                }
              />
            }
          />
        </CardContent>
      </Card>

      {/* Modal - Create / Edit Assignment */}
      <Modal
        isOpen={showModal}
        onClose={closeModal}
        title={editingId ? "Edit Assignment" : "Assign Teacher to Subject"}
        footer={
          <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 w-full">
            <Button variant="secondary" onClick={closeModal} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button
              type="submit"
              form="assignmentForm"
              variant="primary"
              disabled={saving}
              className="w-full sm:w-auto"
            >
              {saving ? "Saving…" : editingId ? "Update" : "Create Assignment"}
            </Button>
          </div>
        }
      >
        <form id="assignmentForm" onSubmit={handleSubmit} className="space-y-4 pt-1">
          <Select
            label="Teacher"
            required
            options={[
              { label: "Select teacher", value: "" },
              ...selectStaffOptions.slice(1),
            ]}
            value={form.staffId}
            onChange={(val) => handleFormChange("staffId", val)}
          />

          <Select
            label="Subject"
            required
            options={[
              { label: "Select subject", value: "" },
              ...selectSubjectOptions.slice(1),
            ]}
            value={form.subjectId}
            onChange={(val) => handleFormChange("subjectId", val)}
          />

          {!editingId && (
            <>
              <Select
                label="Class"
                required
                options={[
                  { label: "Select class", value: "" },
                  ...selectClassOptions.slice(1),
                ]}
                value={form.classId}
                onChange={(val) => handleFormChange("classId", val)}
              />

              <Select
                label="Section"
                required
                options={selectFormSectionsOptions}
                value={form.sectionId}
                disabled={!form.classId}
                onChange={(val) => handleFormChange("sectionId", val)}
              />
            </>
          )}

          {editingId && (
            <div className="space-y-2 pt-1">
              <Checkbox
                id="isActive"
                label="Active"
                checked={form.isActive}
                onChange={(e) => handleFormChange("isActive", e.target.checked)}
              />
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
                Class and section cannot be changed after creation. Delete and re-assign if needed.
              </p>
            </div>
          )}
        </form>
      </Modal>
    </div>
  );
};

export default TeacherSubject;